import { InvoiceItemDto } from '../types/Invoice'

// Mirrors sk.ladient.amani.util.InvoiceMath on the backend, which recomputes and stores these
// amounts - so what the form shows is what the invoice will say.

export const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100

// A discount outside 0-100 % is read as the nearest one inside it, a missing one as none
export const normalizeDiscount = (discount: number | string | undefined | null): number => {
    const value = Number(discount)
    if (!isFinite(value) || value <= 0) {
        return 0
    }
    return Math.min(value, 100)
}

const applyDiscount = (amount: number, discount: number | string | undefined | null): number =>
    amount * (100 - normalizeDiscount(discount)) / 100

const vat = (net: number, rate: number | string | undefined | null): number =>
    round2(net * (Number(rate) || 0) / 100)

export interface LineAmounts {
    totalWithoutDph: number
    dphValue: number
    totalWithDph: number
}

// Quantity times unit price, less the line discount, with VAT on what is left
export function lineAmounts(item: Pick<InvoiceItemDto, 'quantity' | 'unitPrice' | 'dph' | 'discount'>): LineAmounts {
    const net = round2(applyDiscount((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0), item.discount))
    const itemVat = vat(net, item.dph)
    return {totalWithoutDph: net, dphValue: itemVat, totalWithDph: round2(net + itemVat)}
}

export interface InvoiceTotals {
    itemsWithoutDph: number
    itemsDph: number
    itemsWithDph: number
    totalWithoutDph: number
    dphValue: number
    totalWithDph: number
}

// The document discount lowers each line's net amount and the VAT is worked out again from it
export function invoiceTotals(items: InvoiceItemDto[] | undefined, discount: number | string | undefined | null): InvoiceTotals {
    const totals: InvoiceTotals = {
        itemsWithoutDph: 0, itemsDph: 0, itemsWithDph: 0, totalWithoutDph: 0, dphValue: 0, totalWithDph: 0
    }

    for (const item of items ?? []) {
        const line = lineAmounts(item)
        const net = round2(applyDiscount(line.totalWithoutDph, discount))
        const netVat = vat(net, item.dph)

        totals.itemsWithoutDph += line.totalWithoutDph
        totals.itemsDph += line.dphValue
        totals.totalWithoutDph += net
        totals.dphValue += netVat
    }

    totals.itemsWithoutDph = round2(totals.itemsWithoutDph)
    totals.itemsDph = round2(totals.itemsDph)
    totals.itemsWithDph = round2(totals.itemsWithoutDph + totals.itemsDph)
    totals.totalWithoutDph = round2(totals.totalWithoutDph)
    totals.dphValue = round2(totals.dphValue)
    totals.totalWithDph = round2(totals.totalWithoutDph + totals.dphValue)

    return totals
}
